/**
 * liveAudioPlayback.ts
 * Plays a real-time PCM16 audio stream with minimal latency.
 * Used by the app to receive live broadcast announcements from the server.
 */

const SAMPLE_RATE = 16_000;
/** Initial buffer ahead of now before scheduling first chunk (seconds). */
const PLAYBACK_BUFFER_AHEAD_S = 0.1;

let audioCtx: AudioContext | null = null;
let nextPlayTime = 0;
let active = false;

function getCtx(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
  }
  return audioCtx;
}

/**
 * Begin live playback. Call when broadcast:start is received.
 */
export function startLivePlayback(): void {
  active = true;
  const ctx = getCtx();
  if (ctx.state === 'suspended') ctx.resume();
  nextPlayTime = ctx.currentTime + PLAYBACK_BUFFER_AHEAD_S;
}

/**
 * Schedule a PCM16 (Int16 LE) audio chunk for playback.
 * @param data Raw PCM16 bytes from the broadcast stream.
 */
export function addPCMChunk(data: ArrayBuffer): void {
  if (!active) return;
  try {
    const ctx = getCtx();
    const int16 = new Int16Array(data);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768;
    }
    const buffer = ctx.createBuffer(1, float32.length, SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const startAt = Math.max(nextPlayTime, ctx.currentTime + 0.01);
    source.start(startAt);
    nextPlayTime = startAt + float32.length / SAMPLE_RATE;
  } catch {
    // Ignore playback errors on individual chunks
  }
}

/**
 * Stop live playback.
 */
export function stopLivePlayback(): void {
  active = false;
  nextPlayTime = 0;
}
